import React from 'react';
import {
	Grid,
	TextField,
	Box,
	Typography,
	InputAdornment,
	Divider,
} from '@mui/material';
import { HairServiceInput } from '../../gql/graphql';

interface PricingTabProps {
	serviceData: Partial<HairServiceInput>;
	updateServiceData: (updates: Partial<HairServiceInput>) => void;
}

function PricingTab({ serviceData, updateServiceData }: PricingTabProps) {
	const handleChange = (field: keyof HairServiceInput) => (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const value = event.target.type === 'number' ? parseFloat(event.target.value) : event.target.value;
		updateServiceData({ [field]: value });
	};

	return (
		<Box>
			<Typography variant="h6" gutterBottom>
				Pricing & Duration
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				Set pricing and duration for this service
			</Typography>

			<Grid container spacing={3}>
				{/* Service Price */}
				<Grid item xs={12} md={6}>
					<TextField
						fullWidth
						required
						label="Service Price"
						name="price"
						type="number"
						value={serviceData.price || ''}
						onChange={handleChange('price')}
						InputProps={{
							startAdornment: <InputAdornment position="start">₹</InputAdornment>,
						}}
						helperText="Price charged to customers"
					/>
				</Grid>

				{/* Duration */}
				<Grid item xs={12} md={6}>
					<TextField
						fullWidth
						required
						label="Duration"
						name="duration_minutes"
						type="number"
						value={serviceData.duration_minutes || ''}
						onChange={handleChange('duration_minutes')}
						InputProps={{
							endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
						}}
						helperText="Estimated time to complete service"
					/>
				</Grid>

				{/* Duration in Hours Display */}
				{serviceData.duration_minutes && serviceData.duration_minutes > 0 && (
					<Grid item xs={12}>
						<Box
							sx={{
								p: 2,
								bgcolor: 'primary.lighter',
								borderRadius: 1,
							}}
						>
							<Typography variant="subtitle2" gutterBottom>
								Duration Summary
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{Math.floor(serviceData.duration_minutes / 60) > 0 && 
									`${Math.floor(serviceData.duration_minutes / 60)} hour${Math.floor(serviceData.duration_minutes / 60) > 1 ? 's' : ''} `}
								{serviceData.duration_minutes % 60 > 0 && 
									`${serviceData.duration_minutes % 60} minute${serviceData.duration_minutes % 60 > 1 ? 's' : ''}`}
							</Typography>
						</Box>
					</Grid>
				)}

				<Grid item xs={12}>
					<Divider sx={{ my: 2 }} />
					<Typography variant="h6" gutterBottom>
						Tax & Commission
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						Set tax and commission percentages for this service
					</Typography>
				</Grid>

				{/* Tax Percentage */}
				<Grid item xs={12} md={6}>
					<TextField
						fullWidth
						label="Tax Percentage"
						name="taxPercentage"
						type="number"
						value={serviceData.taxPercentage || 0}
						onChange={handleChange('taxPercentage')}
						InputProps={{
							endAdornment: <InputAdornment position="end">%</InputAdornment>,
						}}
						helperText="GST or applicable tax rate"
					/>
				</Grid>

				{/* Commission Percentage */}
				<Grid item xs={12} md={6}>
					<TextField
						fullWidth
						label="Stylist Commission"
						name="commissionPercentage"
						type="number"
						value={serviceData.commissionPercentage || 0}
						onChange={handleChange('commissionPercentage')}
						InputProps={{
							endAdornment: <InputAdornment position="end">%</InputAdornment>,
						}}
						helperText="Percentage commission paid to stylists"
					/>
				</Grid>
			</Grid>
		</Box>
	);
}

export default PricingTab;
